/**
 * Thin client for the OIDC / OAuth2 endpoints of the AuthProvider.
 *
 * Everything here is intentionally framework-agnostic and does NOT touch
 * storage / axios instances — see `src/util/auth.ts` for that.
 */
import {
  getOidcPostLogoutRedirectUri,
  getOidcRedirectUri,
  OIDC_AUTHORITY,
  OIDC_CLIENT_ID,
  OIDC_SCOPE,
} from '../util/environment';
import { TokenResponse } from '../interface/response/TokenResponse';
import {
  FORM_URLENCODED_CONTENT_TYPE,
  OAUTH_GRANT_TYPE,
  OAUTH_PARAM,
  OAUTH_RESPONSE_TYPE,
  OIDC_ENDPOINT,
  PKCE_CHALLENGE_METHOD,
} from '../constant/auth-constants';

const trimSlash = (s: string) => s.replace(/\/+$/, '');

const authorityBase = () => trimSlash(OIDC_AUTHORITY);

export const buildAuthorizeUrl = (params: {
  codeChallenge: string;
  state: string;
}): string => {
  const qs = new URLSearchParams({
    [OAUTH_PARAM.CLIENT_ID]: OIDC_CLIENT_ID,
    [OAUTH_PARAM.RESPONSE_TYPE]: OAUTH_RESPONSE_TYPE.CODE,
    [OAUTH_PARAM.SCOPE]: OIDC_SCOPE,
    [OAUTH_PARAM.REDIRECT_URI]: getOidcRedirectUri(),
    [OAUTH_PARAM.CODE_CHALLENGE_METHOD]: PKCE_CHALLENGE_METHOD.S256,
    [OAUTH_PARAM.CODE_CHALLENGE]: params.codeChallenge,
    [OAUTH_PARAM.STATE]: params.state,
  });

  const url = `${authorityBase()}${OIDC_ENDPOINT.AUTHORIZE}?${qs.toString()}`;

  // eslint-disable-next-line no-console
  console.info('[OIDC] authorize URL:', url);
  return url;
};

export const buildLogoutUrl = (idTokenHint?: string): string => {
  const qs = new URLSearchParams({
    [OAUTH_PARAM.POST_LOGOUT_REDIRECT_URI]: getOidcPostLogoutRedirectUri(),
    [OAUTH_PARAM.CLIENT_ID]: OIDC_CLIENT_ID,
  });

  if (idTokenHint) {
    qs.append(OAUTH_PARAM.ID_TOKEN_HINT, idTokenHint);
  }

  return `${authorityBase()}${OIDC_ENDPOINT.END_SESSION}?${qs.toString()}`;
};

const postToTokenEndpoint = async (body: URLSearchParams): Promise<TokenResponse> => {
  const response = await fetch(`${authorityBase()}${OIDC_ENDPOINT.TOKEN}`, {
    method: 'POST',
    headers: { 'Content-Type': FORM_URLENCODED_CONTENT_TYPE },
    body,
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok || !data.access_token) {
    const description = data?.error_description || data?.error || response.statusText;
    throw new Error(`Token endpoint error: ${description}`);
  }

  return data as TokenResponse;
};

export const exchangeCodeForTokens = (params: {
  code: string;
  codeVerifier: string;
}): Promise<TokenResponse> => {
  const body = new URLSearchParams({
    [OAUTH_PARAM.GRANT_TYPE]: OAUTH_GRANT_TYPE.AUTHORIZATION_CODE,
    [OAUTH_PARAM.CLIENT_ID]: OIDC_CLIENT_ID,
    [OAUTH_PARAM.REDIRECT_URI]: getOidcRedirectUri(),
    [OAUTH_PARAM.CODE]: params.code,
    [OAUTH_PARAM.CODE_VERIFIER]: params.codeVerifier,
  });

  return postToTokenEndpoint(body);
};

export const refreshTokens = (refreshToken: string): Promise<TokenResponse> => {
  const body = new URLSearchParams({
    [OAUTH_PARAM.GRANT_TYPE]: OAUTH_GRANT_TYPE.REFRESH_TOKEN,
    [OAUTH_PARAM.CLIENT_ID]: OIDC_CLIENT_ID,
    [OAUTH_PARAM.REFRESH_TOKEN]: refreshToken,
  });

  return postToTokenEndpoint(body);
};
